// components/SiteSelector.tsx
import React, { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import {
  CircularProgress,
  Box,
  SelectChangeEvent,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  TextField,
  Autocomplete,
  Button
} from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchSites, fetchMoreSites, clearSites, Site } from '@/store/slices/siteSlice';
import { setSelectedSite, setSelectedSiteName, setSelectedSiteGDM, setSelectedSiteObject } from '@/store/slices/selectionSlice';
import {
  selectSites,
  selectSiteLoading,
  selectSiteError,
  selectSelectedCustomerId,
  selectSelectedSiteId,
  selectSelectedSiteObject
} from '@/store/selectors';
import { useTranslation } from "react-i18next";
import { RootState } from '@/store/store';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import MiniLoader from '../MiniLoader';
import { useSelection } from '@/hooks/useSelection';
import { fetchLocationsAPI } from '@/services/customers';
import CustomerService from "@/services/customers";
import localconfig from "@/config/local-config.json";

interface SiteSelectorProps {
  label?: string;
  fullWidth?: boolean;
  disabled?: boolean;
}

export const SiteSelector: React.FC<SiteSelectorProps> = React.memo(({
  label = 'Select Site',
  fullWidth = true,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { getFeatureActions } = usePermissions();
  const sites = useAppSelector(selectSites);
  const loading = useAppSelector(selectSiteLoading);
  const error = useAppSelector(selectSiteError);
  const loadingMore = useAppSelector((state: RootState) => state.site.loadingMore);
  const hasMore = useAppSelector((state: RootState) => state.site.hasMore);
  const currentPage = useAppSelector((state: RootState) => state.site.currentPage);
  const totalCount = useAppSelector((state: RootState) => state.site.totalCount);
  const selectedCustomerId = useAppSelector(selectSelectedCustomerId);
  const selectedType = useAppSelector((state: RootState) => state.selection.selectedType);
  const selectedSiteId = useAppSelector(selectSelectedSiteId);
  const selectedSite = useAppSelector(selectSelectedSiteObject);  // Smart selector handles persistence
  const { data: userData } = useAppSelector((state: RootState) => state.user);
  const customers = useAppSelector((state: RootState) => state.customer);
  const siteStates = useAppSelector((state: RootState) => state.site);
  const { pathname } = useLocation();
  const { getAccessTokenSilently } = useAuth0();
  const { setInitialLoad } = useSelection();
  const {initialLoad} = useAppSelector((state: RootState) => state.selection);

  const showgdmnumber = useMemo(() => getFeatureActions('Navigation', 'View Customer GDM ID', 'Customer Navigation'), [getFeatureActions]);
  const isDeviceDetailPage = pathname.startsWith("/devices/");
  const isSearchResultsPage = pathname.startsWith("/search-results/");
  const isWalkdownDetailPage = /^\/walkdowns\/(?!qr-codes)[^/]+$/.test(pathname);
  const isTurnAroundDetailPage = /^\/turnarounds\/[^/]+$/.test(pathname);
  const freezeForm = isDeviceDetailPage || isSearchResultsPage || isWalkdownDetailPage || isTurnAroundDetailPage;
  const [dialogOpen, setDialogOpen] = useState(false);
  const closeDialog = () => setDialogOpen(false);
  const [country, setCountry] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [city, setCity] = useState("");
  const [siteName, setSiteName] = useState("");
  const [isSearchingApi, setIsSearchingApi] = useState(false);
  const filterTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevFiltersRef = useRef({
      country: "",
      stateProvince: "",
      city: "",
      siteName: "",
    });
  const [isFilteringApi, setIsFilteringApi] = useState(false);
  const userId = userData?.id;

    // New state for dropdown options from API
    const [countries, setCountries] = useState<string[]>([]);
    const [states, setStates] = useState<string[]>([]);
    const [cities, setCities] = useState<string[]>([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);

    // Local state for modal site management (similar to moveDeviceModal)
    const [localModalSites, setLocalModalSites] = useState<any[]>([]);
    const [modalSitesLoading, setModalSitesLoading] = useState(false);
    const [modalSitesLoadingMore, setModalSitesLoadingMore] = useState(false);
    const [modalSitesCurrentPage, setModalSitesCurrentPage] = useState(0);
    const [modalSitesHasMore, setModalSitesHasMore] = useState(false);
    const [modalSitesTotalCount, setModalSitesTotalCount] = useState(0);
    const loadingMoreRef = useRef(false);

  const siteList: Site[] = useMemo(() => {
    if (!sites) return [];
    console.log('Sites in SiteSelector:', sites);
    const arr = Array.isArray(sites) ? sites : (sites as any).sites;
    return Array.isArray(arr) ? arr : [];
  }, [sites]);

  const handleLoadMore = useCallback(async () => {
    if (!userId || !selectedType || !selectedCustomerId || loadingMore || !hasMore) return;
    
    const accessToken = await getAccessTokenSilently();
    dispatch(fetchMoreSites({
      userId: userId,
      type: selectedType,
      id: selectedCustomerId,
      accessToken: accessToken,
      page: currentPage + 1,
      limit: 100,
      search: siteName?.trim() || undefined,
      country: country || undefined,
      province: stateProvince || undefined,
      city: city || undefined
    }));
  }, [userId, selectedType, selectedCustomerId, currentPage, loadingMore, hasMore, siteName, country, stateProvince, city, dispatch, getAccessTokenSilently]);

  // Fetch sites when customer changes
  useEffect(() => {
    
   
    async function run() {
      if (selectedCustomerId && selectedType && userId) {
        const accessToken = await getAccessTokenSilently();        
        dispatch(fetchSites({
          userId: userId,
          type: selectedType as string,
          id: selectedCustomerId,
          accessToken: accessToken,
          loadDefault: !selectedSiteId,
        })).then(()=>{
          dispatch(setInitialLoad(false));
        })
        
      } else {
        // Clear sites when no customer is selected
        dispatch(clearSites());
      }
    }

    run();

  }, [selectedCustomerId, userData, dispatch]);


  useEffect(() => {
    async function run() {
      console.log('SiteSelector: checking for default site selection', selectedCustomerId, siteList, selectedSiteId, initialLoad);
      if (selectedCustomerId && siteList.length > 0) {
        // Try to find default:true site
        let siteToSelect = siteList.find((x: Site) => x.default === true);
        if (!(initialLoad && selectedSiteId)) {
          if (siteToSelect) {
            dispatch(setSelectedSite(siteToSelect?.site_id || null));
            dispatch(setSelectedSiteName(siteToSelect?.site_name || null));
            dispatch(setSelectedSiteGDM(siteToSelect?.gdm_customer_number || null));
            dispatch(setSelectedSiteObject(siteToSelect));
          }
        }
        
      }
    }
    run();
  }, [selectedCustomerId, siteList, dispatch]);

  const handleChange = useCallback((event: SelectChangeEvent<string>) => {
    const siteId = event.target.value || null;
    const site = siteList.find(s => s.site_id === siteId);
    dispatch(setSelectedSite(siteId));
    dispatch(setSelectedSiteName(site?.site_name || null));
    dispatch(setSelectedSiteGDM(site?.gdm_customer_number || null));
    if (site) {
      dispatch(setSelectedSiteObject(site));
    }
  }, [dispatch, siteList]);

  const handleSiteRemoval = useCallback((event: any) => {
    if (freezeForm) return;
    dispatch(setSelectedSite(null));
    dispatch(setSelectedSiteName(null));
    dispatch(setSelectedSiteGDM(null));
    dispatch(setSelectedSiteObject(null));
  },[dispatch, freezeForm]);

    // Fetch sites for modal (similar to moveDeviceModal pattern)
    const fetchSitesForModal = useCallback(
      async (
        page: number = 0,
        append: boolean = false,
        search?: string,
        countryFilter?: string,
        provinceFilter?: string,
        cityFilter?: string,
      ) => {
        if (!selectedCustomerId || !selectedType || !userId) return;

        if (append) {
          setModalSitesLoadingMore(true);
        } else {
          setModalSitesLoading(true);
          if (!append) {
            setLocalModalSites([]);
            setModalSitesCurrentPage(0);
            loadingMoreRef.current = false;
          }
        }

        try {
          const accessToken = await getAccessTokenSilently();
          const token = import.meta.env.DEV
            ? localconfig.accessToken
            : accessToken;
          const customerService = new CustomerService(undefined, token);
          const response = await customerService.getSites(
            userId,
            selectedType as string,
            selectedCustomerId,
            false,
            page,
            100,
            search,
            countryFilter,
            provinceFilter,
            cityFilter,
            "Y", // pagination parameter - key difference!
          );

          const siteArray = response?.sites || [];
          const totalCount = response?.total || 0;

          console.log("Modal fetchSites response:", {
            siteArray,
            totalCount,
            response,
          });

          const mappedSites = siteArray.map((site: any) => {
            const {
              site_id = 0,
              gdm_customer_number = "",
              site_name = "",
              address_line_1 = "",
              address_line_2 = "",
              address_line_3 = "",
              city = "",
              state = "",
              postal_code = "",
              country = "",
            } = site || {};

            return {
              site_id: String(site_id),
              id: String(site_id),
              gdm_customer_number: gdm_customer_number,
              site_name: site_name,
              address_line_1,
              address_line_2,
              address_line_3,
              city,
              state,
              postal_code,
              country,
              default: site.default || false,
            };
          });

          if (append) {
            setLocalModalSites((prev) => {
              const combined = [...prev, ...mappedSites];
              setModalSitesHasMore(totalCount > combined.length);
              return combined;
            });
            setModalSitesCurrentPage(page);
          } else {
            setLocalModalSites(mappedSites);
            setModalSitesCurrentPage(page);
            setModalSitesHasMore(totalCount > mappedSites.length);
          }

          setModalSitesTotalCount(totalCount);
        } catch (err) {
          console.error("Error fetching sites for modal:", err);
          if (!append) {
            setLocalModalSites([]);
            setModalSitesTotalCount(0);
          }
        } finally {
          if (append) {
            setModalSitesLoadingMore(false);
            loadingMoreRef.current = false;
          } else {
            setModalSitesLoading(false);
          }
        }
      },
      [selectedCustomerId, selectedType, userId, getAccessTokenSilently],
    );

    const handleModalSiteListScroll = useCallback(
      (event: React.UIEvent<HTMLDivElement>) => {
        const listboxNode = event.currentTarget;
        if ( loadingMoreRef.current || modalSitesLoadingMore || !modalSitesHasMore){
          return;
        }

        // Guard: Only trigger if there's scrollable content (list height > viewport height)
        if (listboxNode.scrollHeight <= listboxNode.clientHeight) {
          return;
        }

        const scrollBottom =
          listboxNode.scrollHeight -
          listboxNode.scrollTop -
          listboxNode.clientHeight;

        // Trigger load more when user is within 50px of bottom (increased threshold for safety)
      if (scrollBottom < 50) {
      loadingMoreRef.current = true;
      fetchSitesForModal(
        modalSitesCurrentPage + 1,
        true,
        siteName?.trim() || undefined,
        country || undefined,
        stateProvince || undefined,
        city || undefined,
      );
    }
  },[
      modalSitesLoadingMore,
      modalSitesHasMore,
      modalSitesCurrentPage,
      fetchSitesForModal,
      siteName,
      country,
      stateProvince,
      city,
    ]);

const handleSiteSelect = async () => {
      // Clear any pending filter timeout
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }

      // Open dialog first
      setDialogOpen(true);

    // Reset filters
    setCountry("");
    setStateProvince("");
    setCity("");
    setSiteName("");
    setIsSearchingApi(false);
    setIsFilteringApi(false);

      prevFiltersRef.current = {
        country: "",
        stateProvince: "",
        city: "",
        siteName: "",
      };

      // Clear dropdown options
      setStates([]);
      setCities([]);

      // Fetch countries for dropdowns
      await fetchCountries();

      // Fetch sites using direct API call (like moveDeviceModal)
      await fetchSitesForModal(0, false);
    };

  const handleSiteSelectWithReset = (site: Site) => {
    dispatch(setSelectedSite(site?.site_id || null));
    dispatch(setSelectedSiteName(site?.site_name || null));
    dispatch(setSelectedSiteGDM(site?.gdm_customer_number || null));
    dispatch(setSelectedSiteObject(site));
    closeDialog();
  };

  const selectedCustomer = customers?.data?.customers.find(c => c.id === selectedCustomerId);

  const addressLine = [
    selectedSite?.address_line_1,
    selectedSite?.address_line_2,
    selectedSite?.address_line_3,
  ].filter(Boolean).join(" ");
  const cityStateZip = [
    selectedSite?.city,
    selectedSite?.state,
    selectedSite?.postal_code,
  ].filter(Boolean).join(", ");

    // Fetch countries from API when dialog opens
    const fetchCountries = useCallback(async () => {
      if (!userId || !selectedCustomerId || !selectedType) return;

      setLoadingCountries(true);
      try {
        const accessToken = await getAccessTokenSilently();
        const countriesData = await fetchLocationsAPI(
          userId,
          Number(selectedCustomerId),
          selectedType as "customers" | "site_groups",
          "countries",
          undefined,
          undefined,
          undefined,
          accessToken,
        );
        console.log("Fetched countries:", countriesData);
        setCountries(Array.isArray(countriesData) ? countriesData : []);
      } catch (error) {
        console.error("Error fetching countries:", error);
        setCountries([]);
      } finally {
        setLoadingCountries(false);
      }
    }, [userId, selectedCustomerId, selectedType, getAccessTokenSilently]);

    // Fetch states from API when country changes
    const fetchStates = useCallback(
      async (selectedCountry: string) => {
        if (!userId || !selectedCustomerId || !selectedCountry || !selectedType ) {
          setStates([]);
          return;
        }

        setLoadingStates(true);
        try {
          const accessToken = await getAccessTokenSilently();
          const statesData = await fetchLocationsAPI(
            userId,
            Number(selectedCustomerId),
            selectedType as "customers" | "site_groups",
            "states",
            selectedCountry,
            undefined,
            undefined,
            accessToken,
          );
          console.log("Fetched states:", statesData);
          setStates(Array.isArray(statesData) ? statesData : []);
        } catch (error) {
          console.error("Error fetching states:", error);
          setStates([]);
        } finally {
          setLoadingStates(false);
        }
      },
      [userId, selectedCustomerId, selectedType, getAccessTokenSilently],
    );

    // Fetch cities from API when state changes
    const fetchCities = useCallback(
      async (selectedCountry: string, selectedState: string) => {
        if (
          !userId ||
          !selectedCustomerId ||
          !selectedCountry ||
          !selectedState ||
          !selectedType
        ) {
          setCities([]);
          return;
        }

        setLoadingCities(true);
        try {
          const accessToken = await getAccessTokenSilently();
          const citiesData = await fetchLocationsAPI(
            userId,
            Number(selectedCustomerId),
            selectedType as "customers" | "site_groups",
            "cities",
            selectedCountry,
            selectedState,
            undefined,
            accessToken,
          );
          console.log("Fetched cities:", citiesData);
          setCities(Array.isArray(citiesData) ? citiesData : []);
        } catch (error) {
          console.error("Error fetching cities:", error);
          setCities([]);
        } finally {
          setLoadingCities(false);
        }
      },
      [userId, selectedCustomerId, selectedType, getAccessTokenSilently],
    );

    // Fetch states when country changes
    useEffect(() => {
      if (country) {
        fetchStates(country);
      } else {
        setStates([]);
        setCities([]);
      }
    }, [country, fetchStates]);

    // Fetch cities when state changes
    useEffect(() => {
      if (country && stateProvince) {
        fetchCities(country, stateProvince);
      } else {
        setCities([]);
      }
    }, [country, stateProvince, fetchCities]);

    // Matching sites - use local modal sites when dialog is open
    const matchingSites = useMemo(() => {
      const sitesToFilter = dialogOpen ? localModalSites : siteList;
      return sitesToFilter.filter(
        (s) =>
          (!country || s.country === country) &&
          (!stateProvince || s.state === stateProvince) &&
          (!city || s.city === city) &&
          (!siteName ||
            s.site_name?.toLowerCase().includes(siteName.toLowerCase())),
      );
    }, [
      dialogOpen,
      localModalSites,
      siteList,
      country,
      stateProvince,
      city,
      siteName,
    ]);

 const shouldSearchApi = useCallback((searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 3) return false;
    // Check if search term exists in current list
    const foundInCurrent = localModalSites.some((site) =>
    site.site_name?.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    // If found in current list, use frontend filter
    if (foundInCurrent) {
    return false;
    }
  // If not found or we have 100+ matches, make API call
   return true;
  },[localModalSites]);

    // API search for site name (triggered on blur)
    const handleSiteNameSearch = useCallback(
      async (searchTerm: string) => {
        const trimmedSearch = searchTerm?.trim() || "";

        // If search term is empty or too short
        if (!trimmedSearch || trimmedSearch.length < 3) {
          // Reload to get full list if there was a previous search
          if (localModalSites.length === 0 || trimmedSearch.length === 0) {
            await fetchSitesForModal(
              0,
              false,
              undefined,
              country || undefined,
              stateProvince || undefined,
              city || undefined,
            );
          }
          return;
        }

        // Make API call with search term
        if (shouldSearchApi(trimmedSearch)) {
          setIsSearchingApi(true);
          try {
            await fetchSitesForModal(
              0,
              false,
              trimmedSearch,
              country || undefined,
              stateProvince || undefined,
              city || undefined,
            );
          } finally {
            setIsSearchingApi(false);
          }
        }
      },
      [
        localModalSites,
        fetchSitesForModal,
        shouldSearchApi,
        country,
        stateProvince,
        city,
      ],
    );

    // API call when country/state/city filters change (only for modal)
    useEffect(() => {
      // Only run when dialog is open
      if (!dialogOpen) return;

    
      const prevFilters = prevFiltersRef.current;
      const currentFilters = { country, stateProvince, city, siteName };

      // Check if filters have actually changed
      const filtersChanged =
        prevFilters.country !== country ||
        prevFilters.stateProvince !== stateProvince ||
        prevFilters.city !== city ||
        prevFilters.siteName !== siteName;

      // Update previous filters
      prevFiltersRef.current = currentFilters;

      // Skip if filters haven't changed
      if (!filtersChanged) return;

      // Check if we had filters before and now all are empty (clearing filters)
      const hadFilters =
        prevFilters.country ||
        prevFilters.stateProvince ||
        prevFilters.city ||
        prevFilters.siteName;
      const allFiltersEmpty = !country && !stateProvince && !city && !siteName;

      // Skip initial render when dialog first opens (all filters empty and no previous filters)
      if (allFiltersEmpty && !hadFilters) return;

      // Clear previous timeout to cancel pending API calls
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
      }
      // Show loading indicator immediately
      setIsFilteringApi(true);

      // Debounce by 300ms to batch cascade state updates (e.g., selecting country clears state/city)
      filterTimeoutRef.current = setTimeout(async () => {
      try {
        await fetchSitesForModal(
          0,
          false,
          siteName?.trim() || undefined,
          country || undefined,
          stateProvince || undefined,
          city || undefined,
        );
        } finally {
          setIsFilteringApi(false);
        }
       }, 300);

      // Cleanup: clear timeout if dependencies change before it fires
      return () => {
        if (filterTimeoutRef.current) {
          clearTimeout(filterTimeoutRef.current);
        }
      };
    }, [
      country,
      stateProvince,
      city,
      siteName,
      dialogOpen,
      fetchSitesForModal,
    ]);

  useEffect(() => {
    return () => {
      if (filterTimeoutRef.current) {
        clearTimeout(filterTimeoutRef.current);
        filterTimeoutRef.current = null;
      }
        setIsFilteringApi(false);
    };
  }, []);

    // Reset modal state when dialog closes
    useEffect(() => {
      if (!dialogOpen) {
        setLocalModalSites([]);
        setModalSitesLoading(false);
        setModalSitesLoadingMore(false);
        setModalSitesCurrentPage(0);
        setModalSitesHasMore(false);
        setModalSitesTotalCount(0);
        setIsFilteringApi(false);
        loadingMoreRef.current = false;
        prevFiltersRef.current = {
          country: "",
          stateProvince: "",
          city: "",
          siteName: "",
        };
      }
    }, [dialogOpen]);

  const formatAddress = (site: Site) => {
    const addressLines = [
      site.address_line_1,
      site.address_line_2,
      site.address_line_3,
    ]
      .filter(Boolean)
      .join(" ");
    const location = [site.city, site.state, site.postal_code, site.country]
      .filter(Boolean)
      .join(", ");
    const fullAddress = [addressLines, location].filter(Boolean).join(", ");
    return site.gdm_customer_number
      ? `${fullAddress} | ${site.gdm_customer_number}`
      : fullAddress;
  };

  const getActionColor = (activeColor: string) =>
    freezeForm ? "#999" : activeColor;

  return (
    <>

      <Box className="csgf-row">
        <Typography className="csgf-label-site">
          {t("site", "SITE")}
        </Typography>
        {(selectedCustomerId && selectedSiteId && !loading && !error) ? (
          <Typography
            className="csgf-action"
            style={{
              color: getActionColor("#00805A"),
              cursor: freezeForm ? "not-allowed" : "pointer",
            }}
          >
            <span
              onClick={() => {
                if (freezeForm) return;
                handleSiteSelect();
              }}
            >
              {t("change", "Change")}
            </span>
            <span className="csgf-separator">|</span>
            <span onClick={handleSiteRemoval}>
              {t("remove", "Remove")}
            </span>
          </Typography>
        ) : (
          <Typography
            className={`csgf-action ${freezeForm
              ? "csgf-disabled"
              : "csgf-enabled"
              }`}
            onClick={() => {
              if (freezeForm || !selectedCustomerId) return;
              handleSiteSelect();
            }}
          >
            {t("select", "Select")}
          </Typography>
        )}
      </Box>
      {

        (selectedCustomerId && loading) ? (
          <Box sx={{ m: 2 }}>
            <CircularProgress size={20} sx={{ color: '#00573d' }} />
          </Box>
        ) : (selectedCustomerId && !loading && !error) ? (
          <>
            {selectedSiteId && selectedSite ? (
              <>
                <Typography className="csgf-site-name">
                  {selectedSite?.site_name}
                </Typography>
                <Typography className="csgf-site-address">

                  {[addressLine, cityStateZip].filter(Boolean).join(", ")}
                  {selectedSite?.gdm_customer_number && showgdmnumber?.canView ? ` | ${selectedSite?.gdm_customer_number}` : ""}

                </Typography>
              </>
            ) : (
              <Typography className="csgf-none">
                {!selectedSiteId
                  ? t("noSiteSelected", "No Site Selected")
                  : t("noDefaultSiteSelected", "No Default Site Selected")}
              </Typography>
            )}
          </>
        ) : (
          <Typography className="csgf-none">
            {t("noneSelected", "None Selected")}
          </Typography>
        )
      }

      {/* Site Select Dialog */}
      {
        (dialogOpen) && (
          <Dialog
            open={dialogOpen}
            onClose={closeDialog}
            maxWidth={"sm"}
            fullWidth
          >
            <DialogTitle className="dialogTitle">
              { t("selectSite", "SELECT SITE")}
              <IconButton onClick={closeDialog} className="closeButton" aria-label="Close">
                <CloseIcon className="closeIcon" />
              </IconButton>
            </DialogTitle>

            <DialogContent
              dividers
              className={"dialogContentSite"}
            >
              {(modalSitesLoading || isFilteringApi) && (
                <Box className="loaderOverlay">
                  <MiniLoader size={32} />
                </Box>
              )}

              {error && (
                <Typography className="errorMessage">
                  {t("errorLoadingData", "Error loading Sites. Please try Again")}
                </Typography>
              )}

              {
                <>
                  <Typography className="sitesHeading">
                    {selectedCustomer?.name} {t("sites", "Sites")}
                  </Typography>
                  <Grid container mt={2} spacing={2}>
                    {/* Country Filter */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography className="filterLabel">
                        {t("country", "COUNTRY")}
                      </Typography>
                      <Autocomplete
                        size="small"
                        options={countries || []}
                        value={country || null}
                        loading={loadingCountries}
                        disabled={loadingCountries}
                        onChange={(event, newValue) => {
                          setCountry(newValue || "");
                          setStateProvince("");
                          setCity("");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder={t("select", "Select")}
                            className="filterSelect"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingCountries ? (
                                    <CircularProgress
                                      size={16}
                                      sx={{ color: "#00573d" }}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        className="filterSelect"
                      />
                    </Grid>

                    {/* State/Province Filter */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography className="filterLabel">
                        {t("stateProvince", "STATE/PROVINCE")}
                      </Typography>
                      <Autocomplete
                        size="small"
                        options={states || []}
                        value={stateProvince || null}
                        loading={loadingStates}
                        disabled={!country || loadingStates}
                        onChange={(event, newValue) => {
                          setStateProvince(newValue || "");
                          setCity("");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder={t("select", "Select")}
                            className="filterSelect"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingStates ? (
                                    <CircularProgress
                                      size={16}
                                      sx={{ color: "#00573d" }}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        className="filterSelect"
                      />
                    </Grid>

                    {/* City Filter */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography className="filterLabel">
                        {t("city", "CITY")}
                      </Typography>
                      <Autocomplete
                        size="small"
                        options={cities || []}
                        value={city || null}
                        loading={loadingCities}
                        disabled={!stateProvince || loadingCities}
                        onChange={(event, newValue) => {
                          setCity(newValue || "");
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            placeholder={t("select", "Select")}
                            className="filterSelect"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingCities ? (
                                    <CircularProgress
                                      size={16}
                                      sx={{ color: "#00573d" }}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        className="filterSelect"
                      />
                    </Grid>

                    {/* Site Name */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography className="filterLabel">
                        {t("siteName", "SITE NAME")}
                      </Typography>
                      <TextField
                        variant="outlined"
                        fullWidth
                        size="small"
                        value={siteName}
                        onChange={(e) => setSiteName(e.target.value)}
                        onBlur={(e) => handleSiteNameSearch(e.target.value)}
                        className="siteNameInput"
                        InputProps={{
                          endAdornment: isSearchingApi ? (
                            <CircularProgress size={16} sx={{ color: '#00573d' }} />
                          ) : null,
                        }}
                      />
                    </Grid>

                    {/* Matching Sites */}
                    <Grid size={{ xs: 12 }}>
                      <Typography>
                        {t("matchingSites", "Matching Sites")} (
                        {modalSitesTotalCount})
                      </Typography>
                    {matchingSites.length > 0 ? (
                      <>
                      <Box className="matchingSitesBox"
                          onScroll={handleModalSiteListScroll}
                          sx={{
                              maxHeight: "400px",
                              overflowY: "auto",
                              mt: 1,
                            }}
                          >
                        {matchingSites.map((site: any, idx: number) => (
                          <Box
                            key={idx}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleSiteSelectWithReset(site)}
                            className="siteCard"
                          >
                            <Typography className="siteCardTitle">
                              {site.site_name}
                            </Typography>
                            <Typography className="siteCardAddress">
                              {formatAddress(site)}
                            </Typography>
                          </Box>
                        ))}
                          {/* Loading indicator at bottom when fetching more */}
                            {modalSitesLoadingMore && (
                              <Box
                              sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  p: 2,
                                }}
                              >
                                <CircularProgress
                                  size={20}
                                  sx={{ color: "#00573d" }}
                                />
                              </Box>
                            )}
                          </Box>
                        </>
                      ) : !modalSitesLoading && modalSitesTotalCount > 0 ? (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: '#f5f5f5',
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ textAlign: 'center', color: '#666' }}
                          >
                            {modalSitesTotalCount} site
                            {modalSitesTotalCount > 1 ? 's' : ''} found
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              textAlign: 'center',
                              color: '#999',
                              mt: 1,
                              fontSize: '0.85rem',
                            }}
                          >
                            Try adjusting your filters to see results
                          </Typography>
                        </Box>
                      ) : (
                        !modalSitesLoading && (
                          <Typography
                            variant="body2"
                            sx={{ mt: 2, textAlign: 'center', color: '#666' }}
                          >
                            {t("noSitesFound", "No sites found")}
                          </Typography>
                        )
                      )}
                    </Grid>
                  </Grid>
                </>
              }
            </DialogContent>
          </Dialog>
        )
      }
    </>
  );

});

SiteSelector.displayName = 'SiteSelector';
export default SiteSelector;